import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import TurndownService from "turndown";
import { useLocalization } from "./LocalizationContext";
import WebSocketService from "../services/WebSocket";
import ApiService from "../services/ApiService.js";

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { chatId } = useParams();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isChatsLoading, setIsChatsLoading] = useState(true);

  const addMessage = useCallback((chatId, message) => {
    setChats((prev) => {
      const updatedChats = prev.map((chat) =>
        chat.id === chatId
          ? {
              ...chat,
              messages: [...(chat.messages || []), message],
              updatedAt: new Date().toISOString(),
            }
          : chat,
      );
      return updatedChats;
    });
  }, []);

  const getAllChats = useCallback(async () => {
    setIsChatsLoading(true);
    try {
      const response = await ApiService.get("threads?include_archived=true");

      if (response.data && Array.isArray(response.data)) {
        const formattedChats = response.data.map((chat) => ({
          id: chat.id.toString(),
          title: chat.name || t("untitledChat"),
          messages: [], // Messages will be fetched on demand
          createdAt: chat.created_at,
          updatedAt: chat.updated_at,
          is_archived: chat.is_archived, // preserve this property
        }));
        setChats(formattedChats);
      }
    } catch (error) {
      setChats([]);
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
      };
    } finally {
      setIsChatsLoading(false);
    }
  }, [t]);

  // Also, let's improve the replaceTempChatWithReal function to handle the transition better
  const replaceTempChatWithReal = (realChat) => {
    setChats((prev) => {
      // Remove any existing temp chat and add real chat at the top
      const filtered = prev.filter((chat) => chat.id !== "new");
      return [realChat, ...filtered];
    });

    // Замість setActiveChat — робимо навігацію, щоб оновився URL,
    // а потім useEffect у ChatProvider синхронізує activeChat і WebSocket
    navigate(`/chat/${realChat.id}`, { replace: true });
  };

  // Replace the startChat function in ChatContext.js with this fixed version

  const startChat = async (query) => {
    if (!query) return { success: false, error: "Query cannot be empty." };
    try {
      const response = await ApiService.post("threads/start", { query });
      console.log("CONTEXT RESPONSE:", response);

      if (response.data) {
        const newThread = await fetchChatMessages(response.data.thread_id);

        const formattedChat = {
          id: newThread.data.id.toString(),
          title: newThread.data.name || query.substring(0, 50),
          messages: newThread.data.messages || [],
          createdAt: newThread.data.created_at,
          updatedAt: newThread.data.updated_at,
          isTemporary: false,
        };

        // Update chats list - add new chat
        setChats((prev) => {
          const filtered = prev.filter((chat) => chat.id !== "new");
          return [formattedChat, ...filtered];
        });

        // Навігація оновить URL -> useEffect оновить activeChat і WebSocket
        navigate(`/chat/${formattedChat.id}`, { replace: true });

        return { success: true, chat: formattedChat };
      }

      return { success: false, error: "Failed to create chat." };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
        status: error.response?.status,
      };
    }
  };

  const prepareNewChat = useCallback(() => {
    setIsTyping(false);

    setChats((prev) => {
      // Remove existing temp chat
      const filtered = prev.filter((chat) => chat.id !== "new");
      return [
        {
          id: "new",
          title: t("newChat") || "Новий чат",
          messages: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          is_archived: false,
          isTemporary: true,
        },
        ...filtered,
      ];
    });

    // Навігація до нового чату
    navigate("/chat/new");

    // WebSocket теж буде скинуто через useEffect (бо chatId буде 'new')
  }, [navigate, t]);

  const switchChat = useCallback(
    (chatId) => {
      setIsTyping(false);

      // Просто навігація - useEffect зробить інше
      navigate(`/chat/${chatId}`);
    },
    [navigate],
  );

  const deleteChat = useCallback(
    async (chatId) => {
      try {
        const response = await ApiService.delete(`threads/${chatId}`);

        if (response.status === 200 || response.status === 204) {
          setChats((prev) => {
            const filtered = prev.filter((chat) => chat.id !== chatId);
            if (chatId === activeChat) {
              if (filtered.length > 0) {
                switchChat(filtered[0].id);
              } else {
                prepareNewChat();
              }
            }
            return filtered;
          });
          return { success: true };
        }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.detail || error?.message,
        };
      }
    },
    [activeChat, switchChat, prepareNewChat],
  );

  // Essential useEffects without cleanup logic
  useEffect(() => {
    getAllChats();
  }, [getAllChats]);

  // Always sync activeChat with chatId from the URL, but тільки після завантаження чатів
  useEffect(() => {
    if (!isChatsLoading && chatId) {
      setActiveChat(chatId);
    }
  }, [chatId, isChatsLoading]);

  const updateChatTitle = useCallback(async (chatId, newTitle) => {
    try {
      const response = await ApiService.put(`threads/${chatId}/rename`, {
        name: newTitle,
      });

      if (response.status === 200 || response.status === 204) {
        setChats((prev) =>
          prev.map((chat) =>
            chat.id === chatId
              ? {
                  ...chat,
                  title: newTitle,
                  updatedAt: new Date().toISOString(),
                }
              : chat,
          ),
        );
        return { success: true };
      }
      return { success: false, error: "Failed to rename chat." };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
      };
    }
  }, []);

  const updateMessage = useCallback((chatId, messageId, updatedMessage) => {
    setChats((prev) =>
      prev.map((chat) => {
        if (chat.id === chatId) {
          const updatedMessages = (chat.messages || []).map((msg) =>
            msg.id === messageId ? { ...msg, ...updatedMessage } : msg,
          );
          return {
            ...chat,
            messages: updatedMessages,
            updatedAt: new Date().toISOString(),
          };
        }
        return chat;
      }),
    );
  }, []);

  const clearChatMessages = useCallback((chatId) => {
    setChats((prev) =>
      prev.map((chat) =>
        chat.id === chatId
          ? {
              ...chat,
              messages: [],
              updatedAt: new Date().toISOString(),
            }
          : chat,
      ),
    );
  }, []);

  const getCurrentChat = useCallback(() => {
    if (!activeChat) return null;
    return chats.find((chat) => chat.id === activeChat) || null;
  }, [chats, activeChat]);

  const getCurrentMessages = useCallback(
    (chatId = null) => {
      const targetChatId = chatId || activeChat;
      if (!targetChatId) return [];

      const chat = chats.find((c) => c.id === targetChatId);
      return chat?.messages || [];
    },
    [chats, activeChat],
  );

  // Search functionality
  const searchInText = useCallback((text, query) => {
    if (!query.trim()) return false;
    return text.toLowerCase().includes(query.toLowerCase().trim());
  }, []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const results = [];

    chats.forEach((chat) => {
      // Search in chat title
      const titleMatch = searchInText(chat.title, searchQuery);

      // Search in messages
      const messageMatches = chat.messages.filter((message) =>
        searchInText(message.text, searchQuery),
      );

      if (titleMatch || messageMatches.length > 0) {
        results.push({
          chat,
          titleMatch,
          messageMatches,
          totalMatches: (titleMatch ? 1 : 0) + messageMatches.length,
        });
      }
    });

    // Sort by relevance (total matches) and recency
    return results.sort((a, b) => {
      if (a.totalMatches !== b.totalMatches) {
        return b.totalMatches - a.totalMatches;
      }
      return new Date(b.chat.updatedAt) - new Date(a.chat.updatedAt);
    });
  }, [chats, searchQuery, searchInText]);

  const filteredChats = useMemo(() => {
    // Helper function to check if chat has at least one user message
    const hasUserMessage = (chat) => {
      if (!chat.messages || chat.messages.length === 0) return false;
      // Check for user messages in different formats (API uses 'sender', local uses 'is_user')
      return chat.messages.some(
        (msg) => msg.is_user === true || msg.sender === "user"
      );
    };

    const baseFilter = (chat) => {
      if (chat.isTemporary) return false;
      // Filter out archived chats
      if (chat.is_archived === true) return false;
      // Filter out chats without user messages (new chats won't appear until first message is sent)
      return hasUserMessage(chat);
    };

    if (!searchQuery.trim()) {
      return chats.filter(baseFilter);
    }
    return searchResults
      .map((result) => result.chat)
      .filter(baseFilter);
  }, [chats, searchQuery, searchResults]);

  const highlightText = useCallback((text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(
      `(${query.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    return text.replace(
      regex,
      '<mark style="background-color: #ffeb3b; color: #000;">$1</mark>',
    );
  }, []);

  const getSearchSnippet = useCallback((text, query, maxLength = 100) => {
    if (!query.trim())
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase().trim();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1)
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;

    const start = Math.max(0, index - 20);
    const end = Math.min(text.length, index + query.length + 20);

    let snippet = text.substring(start, end);
    if (start > 0) snippet = "..." + snippet;
    if (end < text.length) snippet = snippet + "...";

    return snippet;
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const resumeChat = async (threadId, answer) => {
    try {
      await ApiService.post(`threads/resume/${threadId}`, {
        answer,
      });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
      };
    }
  };

  const fetchChatMessages = async (threadId) => {
    if (threadId === "new") {
      return { success: true, data: [] };
    }
    try {
      const response = await ApiService.get(`threads/${threadId}`);

      if (response && response.data.search) {
        const htmlPattern = /<[^>]+>/;
        if (htmlPattern.test(response.data.search.answer)) {
          const turndownService = new TurndownService();
          const html = response.data.search.answer;
          const markdown = turndownService.turndown(html);

          response.data.search.answer = markdown;

          return { success: true, data: response.data };
        }

        return { success: true, data: response.data };
      }

      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
      };
    }
  };

  const archiveChat = useCallback(
    async (chatId) => {
      try {
        const response = await ApiService.put(`threads/${chatId}/archive`);

        if (response.status === 200 || response.status === 204) {
          // Update the chat's is_archived property instead of removing it
          setChats((prev) =>
            prev.map((chat) =>
              chat.id === chatId ? { ...chat, is_archived: true } : chat,
            ),
          );
          // Якщо архівується активний чат, переключити на інший або скинути
          if (chatId === activeChat) {
            if (chats.length > 1) {
              const nextChat = chats.find((chat) => chat.id !== chatId);
              if (nextChat) switchChat(nextChat.id);
            } else {
              prepareNewChat();
            }
          }
          return { success: true };
        }
        return { success: false, error: "Failed to archive chat." };
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.detail || error?.message,
        };
      }
    },
    [activeChat, switchChat, prepareNewChat, chats],
  );

  const unarchiveChat = useCallback(async (chatId) => {
    try {
      console.log("Calling unarchive API for chat:", chatId);
      const response = await ApiService.put(`threads/${chatId}/unarchive`);
      console.log("Unarchive API response:", response);

      if (response.status === 200 || response.status === 204) {
        setChats((prev) =>
          prev.map((chat) =>
            chat.id === chatId ? { ...chat, is_archived: false } : chat,
          ),
        );
        return { success: true };
      }
      return { success: false, error: "Failed to unarchive chat." };
    } catch (error) {
      console.error("Unarchive error:", error);
      // If the API doesn't support unarchive, still update locally so UI matches expectation
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === chatId ? { ...chat, is_archived: false } : chat,
        ),
      );
      return { success: false, error: error.response?.data?.detail || error?.message };
    }
  }, []);

  // Rate search (helpful/unhelpful)
  const rateSearch = useCallback(async (searchId, isHelpful) => {
    console.log("PARAMS:", searchId, isHelpful);
    try {
      const response = await ApiService.patch(`searches/helpful/${searchId}`, {
        search_id: searchId,
        is_helpful: isHelpful,
      });

      console.log("RESPONSE:", response);

      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
      };
    }
  }, []);

  const value = {
    chats,
    activeChat,
    setActiveChat,
    switchChat,
    isTyping,
    setIsTyping,
    startChat,
    replaceTempChatWithReal,
    resumeChat,
    fetchChatMessages,
    prepareNewChat,
    deleteChat,
    updateChatTitle,
    addMessage,
    updateMessage,
    clearChatMessages,
    getCurrentChat,
    getCurrentMessages,
    // Search functionality
    searchQuery,
    setSearchQuery,
    searchResults,
    filteredChats,
    highlightText,
    getSearchSnippet,
    clearSearch,
    isChatsLoading,
    archiveChat,
    unarchiveChat,
    rateSearch,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
